import { type EffectRain, EffectRainType, type EffectSunlight } from '@types';

export type EffectRainParams = Pick<
  EffectRain,
  | 'scale'
  | 'flakes'
  | 'floor'
  | 'speed'
  | 'shake'
  | 'speedVariance'
  | 'wind'
  | 'windDir'
  | 'invert'
>;

export type EffectDefault = Record<'sm' | 'md' | 'lg', EffectRainParams>;

const commonValues = {
  speedVariance: 100,
  floor: 1,
  windDir: 0,
  invert: 0,
};

const commonEffectDefault: EffectDefault = {
  sm: {
    scale: 0.4,
    flakes: 100,
    speed: 0.1,
    shake: 0,
    wind: 1,
    ...commonValues,
  },
  md: {
    scale: 0.5,
    flakes: 250,
    speed: 0.1,
    shake: 0,
    wind: 1,
    ...commonValues,
  },
  lg: {
    scale: 0.5,
    flakes: 350,
    speed: 0.1,
    shake: 0,
    wind: 1,
    ...commonValues,
  },
};

export const RAIN_TYPE_CONFIG: Record<
  EffectRainType,
  {
    id: number;
    name: string;
    src: string;
    icon: string;
    defaultSetting: EffectDefault;
  }
> = {
  [EffectRainType.custom]: {
    id: EffectRainType.custom,
    name: '自定义',
    src: '',
    icon: '',
    defaultSetting: commonEffectDefault,
  },
  [EffectRainType.rain]: {
    id: EffectRainType.rain,
    name: '雨',
    src: '/imgs/xiayu@1x.dcf0aeef71a65ebc1e4c73e2f131fd0f.png',
    icon: '/imgs/ei-rain.262c057b122eb74a6c35499f4b05ec99.png',
    defaultSetting: {
      sm: {
        scale: 0.5,
        flakes: 600,
        speed: 2.5,
        shake: 0,
        wind: 0,
        ...commonValues,
      },
      md: {
        scale: 0.6,
        flakes: 1600,
        speed: 2.5,
        shake: 0,
        wind: 0,
        ...commonValues,
      },
      lg: {
        scale: 0.7,
        flakes: 3000,
        speed: 3.5,
        shake: 0,
        wind: 0,
        ...commonValues,
      },
    },
  },
  [EffectRainType.snow]: {
    id: EffectRainType.snow,
    name: '雪',
    src: '/imgs/xue@1x.bdb8d0bbe41f0dbd3695df37c5112d69.png',
    icon: '/imgs/ei-snow.5882bdf9b0c373fcbacc8891c2f9e709.png',
    defaultSetting: {
      sm: {
        scale: 0.2,
        flakes: 300,
        speed: 0.1,
        shake: 30,
        wind: 1,
        ...commonValues,
      },
      md: {
        scale: 0.2,
        flakes: 550,
        speed: 0.2,
        shake: 30,
        wind: 1,
        ...commonValues,
      },
      lg: {
        scale: 0.2,
        flakes: 850,
        speed: 0.2,
        shake: 30,
        wind: 1,
        ...commonValues,
      },
    },
  },
  [EffectRainType.snowFlower]: {
    id: EffectRainType.snowFlower,
    name: '雪花',
    src: '/imgs/xuehua@1x.11f7cfc4c2d1d16c2a46ac6854db178e.png',
    icon: '/imgs/ei-snowflower.2f46f6f53a487e97880d98f58f464f3e.png',
    defaultSetting: {
      sm: {
        scale: 0.3,
        flakes: 300,
        speed: 0.1,
        shake: 0,
        wind: 0,
        ...commonValues,
      },
      md: {
        scale: 0.4,
        flakes: 600,
        speed: 0.2,
        shake: 0,
        wind: 0,
        ...commonValues,
      },
      lg: {
        scale: 0.3,
        flakes: 850,
        speed: 0.3,
        shake: 0,
        wind: 0,
        ...commonValues,
      },
    },
  },
  [EffectRainType.bubble]: {
    id: EffectRainType.bubble,
    name: '气泡',
    src: '/imgs/qipao@1x.05119dc66c309ddabf086c101ce698bf.png',
    icon: '/imgs/ei-bubble.34f9cfb5efac82094c0d06ba6f3937bc.png',
    defaultSetting: {
      sm: {
        scale: 0.3,
        flakes: 300,
        speed: 0.1,
        shake: 30,
        wind: 1,
        ...commonValues,
        invert: 1,
      },
      md: {
        scale: 0.5,
        flakes: 350,
        speed: 0.1,
        shake: 30,
        wind: 1,
        ...commonValues,
        invert: 1,
      },
      lg: {
        scale: 0.6,
        flakes: 400,
        speed: 0.1,
        shake: 30,
        wind: 1,
        ...commonValues,
        invert: 1,
      },
    },
  },
  [EffectRainType.star]: {
    id: EffectRainType.star,
    name: '星星',
    src: '/imgs/xing@1x.8696ccedb5ab07e0cb06fd245d9afef2.png',
    icon: '/imgs/ei-star.301335281769cbf047da2426a7f76318.png',
    defaultSetting: commonEffectDefault,
  },
  [EffectRainType.smile]: {
    id: EffectRainType.smile,
    name: '笑脸',
    src: '/imgs/xiaolian@1x.88a644632f4fd86e67d5b87dc9857e10.png',
    icon: '/imgs/ei-smile.40558fa9b872244a5067bdda10b610fa.png',
    defaultSetting: commonEffectDefault,
  },
  [EffectRainType.heart]: {
    id: EffectRainType.heart,
    name: '爱心',
    src: '/imgs/hongxing@1x.78db9c42bb60c6e6f0659d98c03b4520.png',
    icon: '/imgs/ei-heart.0fce4273e5dcbe4b4a486d9a9ee2a07c.png',
    defaultSetting: commonEffectDefault,
  },
  [EffectRainType.packet]: {
    id: EffectRainType.packet,
    name: '红包',
    src: '/imgs/hongbao@1x.f1c2c46702ab00c206e5973c47c434c1.png',
    icon: '/imgs/ei-packet.5e1c486895ffc69f0cb45830790c7b6e.png',
    defaultSetting: commonEffectDefault,
  },
  [EffectRainType.money]: {
    id: EffectRainType.money,
    name: '铜钱',
    src: '/imgs/tongqian@1x.949d8ae55f74c6c32726b5652b5b2564.png',
    icon: '/imgs/ei-money.b7879b8a5cafb5e10c95e1390289a5a3.png',
    defaultSetting: commonEffectDefault,
  },
};

export const SUNLIGHT_DRAG_HANDLER = 'sunlight_drag_handler';

export const SUNLIGHT_THUMB: Record<EffectSunlight['style'], string> = {
  blinkstyle1: '/imgs/blinkstyle9.de3ca383d9d30ac4d6d3cc47526ef8a6.png',
  blinkstyle2: '/imgs/blinkstyle2.64470a4a31f3acb838c278fb8c86f48c.png',
  blinkstyle3: '/imgs/blinkstyle7.b2db5b222b91ed099706d39b62679798.png',
  blinkstyle4: '/imgs/blinkstyle6.adad55126995d52f2690de3eb7e94f2f.png',
  blinkstyle5: '/imgs/blinkstyle5.70616c9a0edadb525c42de59ae386b54.png',
  blinkstyle6: '/imgs/blinkstyle4.b3bf1708c21caaa66348df82cb9feecb.png',
  blinkstyle7: '/imgs/blinkstyle3.522ed04cfb7be6b2e0103f90a9f4e750.png',
  blinkstyle8: '/imgs/blinkstyle8.fa350fe6a2099fa362c2085dd5dcad2a.png',
  blinkstyle9: '/imgs/blinkstyle1.5d093d27bc54a31efb23f2471904ce11.png',
};

export const EDITING_EFFECT_ID = 'editing_effect';
